import { useContext, useState, useEffect, ChangeEvent } from "react";
import { useNavigate } from "react-router";
import { Suggestion } from "../Search";
import { MIN_SEARCH_LENGTH } from "@/constants/constants";
import { useGetDrugList } from "@/api-hooks";
import { ThemeSelectorContext } from "@/context/index";
import { useGroupNumber } from "@/utils/useGroupNumber";
import { DrugProps } from "@/api/interfaces/Drug";
import SearchBestPriceSearchTool from "../FindBestPriceSearchTool";

import styles from "./FindBestPriceSearch.module.scss";

const FindBestPriceSearch = (): JSX.Element => {
  const navigate = useNavigate();
  const { client } = useContext(ThemeSelectorContext);

  const [searchString, setSearchString] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [suggestions, setSuggestions] = useState<Suggestion<DrugProps>[]>([]);
  const [selectedDrug, setSelectedDrug] = useState<DrugProps>(null);

  const onSelectDrug = (drugData: DrugProps): void => {
    setSelectedDrug(drugData);
    setSearchTerm(drugData.drug.drug_name);
  };

  const group_number = useGroupNumber();

  const { isFetching, isLoading, data, isSuccess, isError } = useGetDrugList(
    { search: searchString },
    { enabled: searchString !== null && searchString.length >= MIN_SEARCH_LENGTH },
  );

  const getSuggestedDrugsList = async (e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  };

  useEffect(() => {
    if (isSuccess && data && data?.results?.length) {
      setSuggestions(() =>
        data?.results?.map((drug: DrugProps) => {
          return {
            suggestionLabel: drug.drug.drug_name,
            data: {
              ...drug,
            },
          };
        }),
      );
    } else {
      setSuggestions([]);
    }
  }, [data, isSuccess, isError]);

  const goToDrugDetails = () => {
    if (selectedDrug !== null && searchTerm !== null) {
      const searchParams = new URLSearchParams();
      let pathname = "/find-best-price/drug-details";

      searchParams.append("drug_id", selectedDrug.drug.id.toString());

      if (group_number) {
        searchParams.append("group_number", group_number);
      }

      if (client) {
        pathname = `/client/${client}/find-best-price/drug-details`;
      }

      const encodedSearchParams = encodeURIComponent(searchParams.toString());

      navigate({
        pathname: `${pathname}`,
        search: `?${encodedSearchParams}`,
      });
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const invalidSearch = !e.target.value || e.target.value.length < MIN_SEARCH_LENGTH;

    if (invalidSearch) {
      setSearchString(e.target.value);
      setSuggestions([]);
    }
  };

  return (
    <SearchBestPriceSearchTool
      id="find-best-price-drug-search"
      testId="find-best-price-drug-search"
      name="find_best_price_search"
      placeholder="Search for your drug"
      inputClassName={client && styles.FindBestPriceSearch}
      searchBarWrapperClassName={client && styles.FindBestPriceSearchWrapper}
      searchBarButtonClass={client && styles.FindBestPriceSearchButton}
      searchBarMagnifyingGlassClass={client && styles.FindBestPriceSearchIcon}
      getSuggestions={getSuggestedDrugsList}
      suggestions={suggestions}
      textLength={3}
      loading={isLoading || isFetching}
      onSuggestionClick={onSelectDrug}
      onEnterSearch={goToDrugDetails}
      onChange={onChange}
      autoFocus={true}
      displaySearchButton
      searchBarButtonSize="medium"
    />
  );
};

export default FindBestPriceSearch;
